import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  AtualizarCategoriaRequest,
  AtualizarPerfilRequest,
  CriarCategoriaRequest,
  CriarPerfilRequest,
  Perfil,
  PerfilCategoria,
  PerfilFiltros,
  PerfilStatus,
} from '../models/perfil.model';
import {
  CategoriaResponseDto,
  PerfilInvestimentoResponseDto,
} from '../api/models/perfil-investimento-response.dto';
import {
  mapCategoriaDto,
  mapPerfilInvestimentoDto,
  mapStatusToApi,
} from '../mappers/perfil.mapper';
import { HttpClientService } from '../../shared/services/http-client/http-client.service';

const PERFIL_BASE_PATH = '/v1/PerfilInvestimento';

/**
 * Service de domínio dos perfis de investimento. Consome a API real via
 * `HttpClientService` (mesmo padrão de `CampanhaApiService`/`QuestionarioApiService`),
 * que converte erros HTTP em `ApiHttpError` com a mensagem vinda do `ProblemDetails`
 * da API — necessário para os toasts de erro exibirem a mensagem de negócio real
 * (ex.: conflitos de dependência) em vez de um texto genérico.
 *
 * O filtro de `status` é enviado ao backend como código (`A`/`I`) e o de `data`
 * como `dataAtualizacao`. Já `nome` nunca é enviado: é resolvido inteiramente no
 * cliente para garantir busca case-insensitive (a API filtra por igualdade
 * sensível a maiúsculas/minúsculas).
 */
@Injectable({ providedIn: 'root' })
export class PerfilService {
  private readonly httpClient = inject(HttpClientService);

  getPerfis(filtros?: PerfilFiltros): Observable<Perfil[]> {
    return this.httpClient
      .get<PerfilInvestimentoResponseDto[]>(PERFIL_BASE_PATH, {
        params: this.toHttpParams(filtros),
      })
      .pipe(
        map((dtos) => (dtos ?? []).map(mapPerfilInvestimentoDto)),
        map((perfis) => this.aplicarFiltrosLocais(perfis, filtros)),
      );
  }

  /**
   * Consulta por id usando `GET /v1/PerfilInvestimento?id={id}` e retorna o
   * primeiro resultado. Devolve `undefined` quando não encontrado.
   */
  getPerfilById(idPerfil: number): Observable<Perfil | undefined> {
    return this.httpClient
      .get<PerfilInvestimentoResponseDto[]>(PERFIL_BASE_PATH, {
        params: new HttpParams().set('id', String(idPerfil)),
      })
      .pipe(
        map((dtos) => (dtos ?? []).map(mapPerfilInvestimentoDto)),
        map(
          (perfis) =>
            perfis.find((perfil) => perfil.id === idPerfil) ?? perfis[0],
        ),
      );
  }

  /** Lista as categorias de um perfil via `GET /v1/PerfilInvestimento/{idPerfil}/Categoria`. */
  getCategoriasByPerfil(idPerfil: number): Observable<PerfilCategoria[]> {
    return this.httpClient
      .get<CategoriaResponseDto[]>(`${PERFIL_BASE_PATH}/${idPerfil}/Categoria`)
      .pipe(map((dtos) => (dtos ?? []).map((dto) => mapCategoriaDto(dto, idPerfil))));
  }

  createPerfil(payload: CriarPerfilRequest): Observable<Perfil> {
    return this.httpClient
      .post<PerfilInvestimentoResponseDto>(PERFIL_BASE_PATH, payload)
      .pipe(map(mapPerfilInvestimentoDto));
  }

  updatePerfil(
    idPerfil: number,
    payload: AtualizarPerfilRequest,
  ): Observable<Perfil> {
    return this.httpClient
      .put<PerfilInvestimentoResponseDto>(`${PERFIL_BASE_PATH}/${idPerfil}`, payload)
      .pipe(map(mapPerfilInvestimentoDto));
  }

  deletePerfil(idPerfil: number): Observable<void> {
    return this.httpClient.delete<void>(`${PERFIL_BASE_PATH}/${idPerfil}`);
  }

  createCategoria(
    idPerfil: number,
    payload: CriarCategoriaRequest,
  ): Observable<PerfilCategoria> {
    return this.httpClient
      .post<CategoriaResponseDto>(`${PERFIL_BASE_PATH}/${idPerfil}/Categoria`, payload)
      .pipe(map((dto) => mapCategoriaDto(dto, idPerfil)));
  }

  updateCategoria(
    idPerfil: number,
    idCategoria: number,
    payload: AtualizarCategoriaRequest,
  ): Observable<PerfilCategoria> {
    return this.httpClient
      .patch<CategoriaResponseDto>(
        `${PERFIL_BASE_PATH}/${idPerfil}/Categoria/${idCategoria}`,
        payload,
      )
      .pipe(map((dto) => mapCategoriaDto(dto, idPerfil)));
  }

  deleteCategoria(idPerfil: number, idCategoria: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${PERFIL_BASE_PATH}/${idPerfil}/Categoria/${idCategoria}`,
    );
  }

  private toHttpParams(filtros?: PerfilFiltros): HttpParams | undefined {
    if (!filtros) {
      return undefined;
    }

    let params = new HttpParams();
    if (filtros.id != null) {
      params = params.set('id', String(filtros.id));
    }
    if (filtros.nomeExibicao) {
      params = params.set('nomeExibicao', filtros.nomeExibicao);
    }
    if (filtros.descricao) {
      params = params.set('descricao', filtros.descricao);
    }
    if (filtros.status) {
      // Backend espera o código do status: Ativo → 'A', Inativo → 'I'.
      params = params.set('status', mapStatusToApi(filtros.status as PerfilStatus));
    }
    if (filtros.data) {
      // O endpoint recebe a data de atualização como `dataAtualizacao`.
      params = params.set('dataAtualizacao', filtros.data);
    }

    return params.keys().length > 0 ? params : undefined;
  }

  /**
   * Filtro local sobre o retorno da API: `nome` (case-insensitive, nunca enviado
   * ao backend) e, como reforço do que já foi filtrado server-side, `status` e `data`.
   */
  private aplicarFiltrosLocais(perfis: Perfil[], filtros?: PerfilFiltros): Perfil[] {
    if (!filtros) {
      return perfis;
    }

    return perfis.filter((perfil) => {
      const nomeOk =
        !filtros.nome ||
        perfil.nome.toLowerCase().includes(filtros.nome.toLowerCase());
      const statusOk = !filtros.status || perfil.status === filtros.status;
      const dataOk = !filtros.data || perfil.ultimaAtualizacao.startsWith(filtros.data);
      return nomeOk && statusOk && dataOk;
    });
  }
}
